'use client';

import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Calendar, Clock, Users, CheckCircle2 } from 'lucide-react';
import { format, addDays, setHours, setMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';

// Available time slots - Restaurant open 10h-22h30, continuous service
const TIME_SLOTS = [
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
  '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'
];

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

export default function ReserverPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '',
    guests: '2',
    remarks: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate next 30 days for date selection
  const availableDates = Array.from({ length: 30 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'EEE d MMM', { locale: fr }),
    };
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Call the Next.js API route (proxy) instead of backend directly
      // This works in both development and production without CORS issues
      const response = await fetch('/api/reservations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.error || 'Une erreur est survenue lors de la réservation. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('[Reservation] Error:', err);
      setError('Impossible de contacter le serveur. Veuillez réessayer plus tard ou nous contacter directement par téléphone.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />

        <section className="flex-1 flex items-center justify-center py-20 px-4">
          <motion.div
            className="max-w-2xl w-full text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-secondary" />
            </div>

            <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6">
              Réservation Confirmée !
            </h1>

            <div className="bg-card border border-border/40 rounded-lg p-8 mb-8 text-left">
              <h2 className="font-semibold text-xl mb-4">Détails de votre réservation</h2>
              <div className="space-y-3 text-muted-foreground">
                <p><span className="font-semibold text-foreground">Nom :</span> {formData.name}</p>
                <p><span className="font-semibold text-foreground">Email :</span> {formData.email}</p>
                <p><span className="font-semibold text-foreground">Téléphone :</span> {formData.phone}</p>
                <p><span className="font-semibold text-foreground">Date :</span> {format(new Date(formData.date), 'd MMMM yyyy', { locale: fr })}</p>
                <p><span className="font-semibold text-foreground">Heure :</span> {formData.time}</p>
                <p><span className="font-semibold text-foreground">Nombre de personnes :</span> {formData.guests}</p>
                {formData.remarks && (
                  <p><span className="font-semibold text-foreground">Remarques :</span> {formData.remarks}</p>
                )}
              </div>
            </div>

            <p className="text-lg text-muted-foreground mb-8">
              Un email de confirmation vous a été envoyé à <strong className="text-foreground">{formData.email}</strong>
            </p>

            <div className="space-y-4">
              <a
                href="/"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-primary-foreground bg-primary rounded-lg hover:opacity-90 transition-all hover:scale-105"
              >
                Retour à l&apos;accueil
              </a>
            </div>
          </motion.div>
        </section>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Page Header */}
      <section className="relative overflow-hidden bg-card py-20 border-b border-border/40">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            className="font-serif text-5xl md:text-6xl font-bold tracking-tight mb-4"
            {...fadeInUp}
          >
            Réserver une Table
          </motion.h1>
          <motion.p
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Vérifiez la disponibilité et réservez votre table en temps réel
          </motion.p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div className="bg-card border border-border/40 rounded-lg p-8">
                <h2 className="font-serif text-2xl font-semibold mb-6">Vos informations</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                      placeholder="jean.dupont@example.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                      placeholder="+352 12 34 56 789"
                    />
                  </div>
                </div>
              </div>

              {/* Reservation Details */}
              <div className="bg-card border border-border/40 rounded-lg p-8">
                <h2 className="font-serif text-2xl font-semibold mb-6">Détails de la réservation</h2>
                <div className="space-y-6">
                  {/* Date Selection */}
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Date *
                    </label>
                    <select
                      id="date"
                      name="date"
                      required
                      value={formData.date}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                    >
                      {availableDates.map((date) => (
                        <option key={date.value} value={date.value}>
                          {date.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Time Selection */}
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Heure *
                    </label>
                    <select
                      id="time"
                      name="time"
                      required
                      value={formData.time}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                    >
                      <option value="">Sélectionnez une heure</option>
                      {TIME_SLOTS.map((slot) => (
                        <option key={slot} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-secondary mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Tous les créneaux affichés sont disponibles
                    </p>
                  </div>

                  {/* Number of Guests */}
                  <div>
                    <label htmlFor="guests" className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Nombre de personnes *
                    </label>
                    <select
                      id="guests"
                      name="guests"
                      required
                      value={formData.guests}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? 'personne' : 'personnes'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Special Remarks */}
                  <div>
                    <label htmlFor="remarks" className="block text-sm font-medium mb-2">
                      Remarques ou occasion spéciale
                    </label>
                    <textarea
                      id="remarks"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all resize-none"
                      placeholder="Anniversaire, allergie alimentaire, préférences de table..."
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Facultatif - Indiquez-nous si vous célébrez une occasion spéciale ou si vous avez des demandes particulières
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                  <p className="font-semibold">❌ Erreur</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-12 py-4 text-lg font-semibold text-primary-foreground bg-primary rounded-lg hover:opacity-90 transition-all hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? 'Envoi en cours...' : 'Confirmer la Réservation'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
